import type { Hex } from '../contract.js';
import { copy } from '../copy.js';
import { isYou, memberOf, profileOfMember } from '../state/lookup.js';
import type { AppState } from '../state/types.js';
import { color } from '../tokens.js';
import { pfpOf } from './picture.js';
import type { Pfp } from './picture.js';

/** The profile header, ported from `profileHeaderVals` (02 §2.6, §3.29; 04 §13). */

export interface ProfileHeaderVals {
  readonly show: boolean;
  readonly op: number;
  readonly color: Hex;
  readonly picture: Pfp;
  /** Your profile is being edited: the picture is the Look button, name and description are fields. */
  readonly editing: boolean;
  /** The picture opens Look (false when nothing in Look can be changed, 06 §4). */
  readonly pictureButton: boolean;
  readonly lookOn: boolean;
  readonly name: string;
  /** "@handle" next to the name (owner, 4 October 2026); empty when the data has none. */
  readonly handle: string;
  /** The profile's saved name: the page heading, also while the name field is being edited. */
  readonly heading: string;
  readonly desc: string;
  readonly showDesc: boolean;
  readonly nameField: boolean;
  readonly descField: boolean;
  readonly hint: string;
  readonly hintColor: string;
  /** Sign out, on your own profile when not editing (owner decision of 2 October 2026). */
  readonly signOut: boolean;
}

export function profileHeaderVals(st: AppState): ProfileHeaderVals {
  const id = st.profileOf, own = isYou(st, id), pe = own && st.view === 'profile' ? st.pe : null, editing = !!pe, m = memberOf(st, id), caps = st.caps;
  const tint = pe ? pe.color : m.color;
  let hint = '', hintColor: string = color.textFaint;
  if (editing && st.peStatus === 'saving') hint = copy.status.saving;
  else if (editing && st.peStatus === 'failed') { hint = copy.status.changesKept; hintColor = color.danger; }
  return {
    show: st.view === 'profile', op: st.pfx === 'in-pre' || st.lv ? 0 : 1, color: tint, picture: pfpOf(st, tint, pe ? pe.pic : m.pic),
    editing, pictureButton: editing && (caps.presetPictures || caps.pictureUpload || caps.changeColour), lookOn: st.tray === 'look',
    name: pe ? pe.name : m.name, handle: m.handle ? `@${m.handle}` : '', heading: m.name, desc: pe ? pe.desc : profileOfMember(st, id)?.desc ?? '',
    showDesc: caps.profileDescription, nameField: editing && caps.rename, descField: editing && caps.profileDescription,
    hint, hintColor,
    signOut: st.view === 'profile' && own && !st.pe,
  };
}
